import React, { ReactNode } from "react";
import { GoogleButton } from "./GoogleButton";
import { EmailInput } from "./EmailInput";
import { PasswordInput } from "./PasswordInput";

import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

type Props = {
  view: "signin" | "signup" | "forgot";
  email: string;
  setEmail: (email: string) => void;
  emailError?: string;
  password?: string;
  setPassword?: (password: string) => void;
  passwordError?: string;
  showPassword?: boolean;
  setShowPassword?: (show: boolean) => void;
  onForgotClick?: () => void;
  onSubmit: (e: React.FormEvent) => void;
  isLoading?: boolean;
  extraFields?: ReactNode;
  turnstileToken: string;
  setTurnstileToken: (token: string) => void;
};

export const AuthForm: React.FC<Props> = ({
  view,
  email,
  setEmail,
  emailError,
  password,
  setPassword,
  passwordError,
  showPassword,
  setShowPassword,
  onForgotClick,
  onSubmit,
  isLoading = false,
  extraFields,
  turnstileToken,
  setTurnstileToken,
}) => (
  <form onSubmit={onSubmit} className="flex flex-col gap-4 mt-4 w-full max-w-md">
    {(view === "signin" || view === "signup") && (
      <>
        <GoogleButton view={view} />
        <div className="flex items-center text-sm text-muted-foreground justify-center">
          <span>or continue with</span>
        </div>
      </>
    )}

    {extraFields && <>{extraFields}</>}
    
    <EmailInput email={email} setEmail={setEmail} error={emailError} />

    {(view === "signin" || view === "signup") &&
      password !== undefined &&
      setPassword &&
      showPassword !== undefined &&
      setShowPassword && (
        <PasswordInput
          password={password}
          setPassword={setPassword}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          error={passwordError}
        />
      )}

    {view === "signin" && onForgotClick && (
      <button
        type="button"
        className="text-right w-full text-xs text-muted-foreground font-medium underline hover:text-foreground transition-colors duration-200"
        onClick={onForgotClick}
      >
        Forgot password?
      </button>
    )}

    <Button
      type="submit"
      disabled={isLoading || !turnstileToken}
      className="w-full h-12 mt-2 transition-all duration-200 hover:scale-[1.02]"
    >
      {isLoading ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {view === "signin" ? "Signing in..." : view === "signup" ? "Creating account..." : "Sending..."}
        </>
      ) : (
        <>
          {view === "signin" ? "Sign In" : view === "signup" ? "Sign Up" : "Send Password Reset"}
        </>
      )}
    </Button>

  </form>
);
